import { Component, OnInit, HostListener, ViewChild, CUSTOM_ELEMENTS_SCHEMA, NO_ERRORS_SCHEMA } from "@angular/core";
import { CommonModule } from "@angular/common";
import { FormsModule, ReactiveFormsModule } from "@angular/forms";
import { RouterModule } from "@angular/router";
import { ActivatedRoute, Router } from "@angular/router";
import { FormControl, FormGroup, Validators } from "@angular/forms";
import { ToastrService } from "ngx-toastr";
import { UserService } from "../../services/user.service";
import { IUserCrud, IRole } from "../../interfaces/ilogin";
import { ICountry } from "../../interfaces/icountry";
import { DropDownItem } from "../../models/dropdown";
import { Company } from "../../models/company";
import { AppSettings } from "../../appsettings";
import { Roles } from "../../enums/roles";
import { ICanComponentDeactivate } from "../../interfaces/icancomponentdeactivate";
import { Observable } from "rxjs";
import { NgSelectComponent } from "../shared/ng-select/ng-select.component";
import { HeaderComponent } from "../header/header.component";
import { UserRoleToggleDirective } from "../../directives/userroletoggle.directive";
import { ConfirmationModalComponent } from "../confirmationmodal/confirmationmodal.component";

@Component({
  selector: "user",
  templateUrl: "./user.component.html",
  standalone: true,
  imports: [CommonModule, FormsModule, ReactiveFormsModule, RouterModule, NgSelectComponent, HeaderComponent, UserRoleToggleDirective, ConfirmationModalComponent],
  schemas: [CUSTOM_ELEMENTS_SCHEMA, NO_ERRORS_SCHEMA]
})
export class UserComponent implements OnInit, ICanComponentDeactivate {
  userForm: FormGroup;
  user: IUserCrud;
  rolesEnum: any = Roles;
  roles: Object[] = new Array<Object>();
  companies: DropDownItem[] = new Array<DropDownItem>();
  selectedCompany: any;
  selectedRole: any;
  selectedRoleName: string;
  allRoles: IRole[] = new Array();
  title = "";
  countries: ICountry[];
  selectAll: boolean = false;
  @ViewChild("confirmModal") confirmModal;

  constructor(
    private activatedRoute: ActivatedRoute,
    private userService: UserService,
    private toastr: ToastrService,
    private router: Router) {

  }

  ngOnInit(): void {
    this.allRoles = this.activatedRoute.snapshot.data["roles"] as IRole[];
    let companiesData: Company[] = this.activatedRoute.snapshot.data["companies"] as Company[];
    this.countries = this.activatedRoute.snapshot.data["countries"] as ICountry[];
    this.companies = companiesData.map((c: Company) => new DropDownItem(c.id, c.name, null));
    const emailRegex: string = AppSettings.emailRegExp.source;
    this.allRoles.map((e: IRole) => { this.roles.push({ id: e.id, text: e.name }); });

    if (this.activatedRoute.snapshot.data != null && this.activatedRoute.snapshot.data["user"] != null) {
      this.user = this.activatedRoute.snapshot.data["user"] as IUserCrud;
      
      // Set the selected role for ng-select binding
      const selectedRoleObj = this.roles.find(r => r['id'] === this.user.role.id);
      this.selectedRole = selectedRoleObj ? [selectedRoleObj] : [];
      this.selectedRoleName = this.user.role.name;
      
      // Set the selected company for ng-select binding  
      const selectedCompanyObj = this.companies.find(c => c.id === this.user.companyId);
      this.selectedCompany = selectedCompanyObj ? [selectedCompanyObj] : [];

      this.selectAll = (this.user.countries.length === this.countries.length);
    }

    // initialise Form
    this.userForm = new FormGroup({
      id: new FormControl(this.user ? this.user.id : null),
      firstName: new FormControl(this.user ? this.user.firstName : "", [Validators.required]),
      lastName: new FormControl(this.user ? this.user.lastName : "", [Validators.required]),
      email: new FormControl(this.user ? this.user.email : "",
        [Validators.required, Validators.pattern(emailRegex), Validators.required, Validators.minLength(5)]),
      disabled: new FormControl(this.user ? this.user.disabled : false),
      roleId: new FormControl(this.user ? this.user.role.id : "", [Validators.required]),
      companyId: new FormControl(this.user ? this.user.companyId : ""),
      userCountries: new FormGroup({})
    });
    if (this.user) {
      this.countries.forEach(item => {
        let userCountriesGroup: FormGroup = this.userForm.controls["userCountries"] as FormGroup;
        userCountriesGroup.addControl(item.isoCode, new FormControl(this.user.countries.find((c) => {
          return item.id === c;
        }) > 0));
      });
    } else {
      this.countries.forEach(item => {
        let userCountriesGroup: FormGroup = this.userForm.controls["userCountries"] as FormGroup;
        userCountriesGroup.addControl(item.isoCode, new FormControl(true));
      });
      this.selectAll = true;
    }

    this.title = this.user ? "Update user" : "Create user";
  }

  roleSelected(selectedItem: any): void {
    if (selectedItem) {
      (<FormControl>this.userForm.controls["roleId"])
        .setValue(selectedItem.id || selectedItem['id'], { onlySelf: false });
      this.selectedRoleName = selectedItem.text || selectedItem['text'];
      this.selectedRole = [selectedItem];
    }
  }

  companySelected(selectedItem: any): void {
    if (selectedItem) {
      (<FormControl>this.userForm.controls["companyId"])
        .setValue(selectedItem.id || selectedItem['id'], { onlySelf: false });
      this.selectedCompany = [selectedItem];
    }
  }

  saveUser(user: IUserCrud, valid: boolean): void {
    user.role = this.allRoles.filter(i => i.id === user.roleId)[0];
    user.countries = this.getSelectedCountries(user.userCountries);
    const companyFilter: DropDownItem[] = this.companies.filter(i => i.id === user.companyId);
    if (companyFilter.length > 0) {
      user.companyId = companyFilter[0].id;
    }

    if (user.id == null) {
      this.userService.create(user).subscribe(() => {
        this.userForm.reset();
        setTimeout(() => this.toastr.success("User created successfully"));
        this.router.navigate(["./users"]);
      });
    } else {
      this.userService.update(user).subscribe(() => {
        this.userForm.reset();
        setTimeout(() => this.toastr.success("User updated successfully"));
        this.router.navigate(["./users"]);
      });
    }
  }

  resendSignupInvitationEmail(): void {
    this.userService.sendInvitation(this.user).subscribe({
      next: (res) => {
        setTimeout(() => this.toastr.success("Sign-up E-mail resent"));
      },
      error: (error) => {
        setTimeout(() => this.toastr.error("Could not send invitation email"));
      }
    });
  }

  private selectAllCountries(): void {
    this.selectAll = !this.selectAll;
    let userCountriesGroup: FormGroup = this.userForm.controls["userCountries"] as FormGroup;
    Object.keys(userCountriesGroup.controls).forEach(key => {
      userCountriesGroup.controls[key].setValue(this.selectAll);
    });
  }

  private getSelectedCountries(userCountries: ICountry[]): number[] {
    let selectedCountries: number[] = [];
    let checkBoxes: string[] = Object.keys(userCountries);
    for (const key of checkBoxes) {
      if (userCountries[key] === true) {
        const found: ICountry = this.countries.find(c => c.isoCode === key);
        selectedCountries.push(found.id);
      }
    }
    return selectedCountries;
  }


  @HostListener("window:beforeunload")
  canDeactivate(): Observable<boolean> | Promise<boolean> | boolean {
    if (this.userForm.dirty) {
      this.confirmModal.openModal();
      return false;
    } else {
      return true;
    }
  }
  onAccept(): void {
    // reset the the original back to whats currenlty in itemConfigSet, this allows to pass check  in candeactivate
    this.userForm.reset();
    this.router.navigate([this.navigatingTo]);
    this.confirmModal.closeModal();
  }

  onDecline(): void {
    this.confirmModal.closeModal();
  }
  navigatingTo: string;
}

